import { toast } from 'sonner';

/**
 * FRONT-1109 (+ FRONT-1154): pure decision for what a graph-entity save
 * toast should say. `PUT /api/graph/entity/:id` always succeeds when the
 * write itself lands (a rename, an attribute edit), but a write that
 * implicitly tried to un-archive the entity can have that specific part
 * withheld — an active duplicate already exists elsewhere in the graph — and
 * the response then carries a plain-language `stayedArchivedNote` alongside
 * the normal 200 body. Saying nothing about that (a bare "Saved") is exactly
 * the "claimed success, wrote somewhere nothing can see" shape this ticket
 * exists to close: the entity (and the fact just saved to it) stays
 * invisible to the agent snapshot and search until the collision is
 * resolved.
 *
 * FRONT-1154 adds the mirror case: renaming an Inactive entity onto a name
 * that does NOT collide lets the implicit un-archive through — the entity
 * comes back active, with no `stayedArchivedNote` at all — and the board
 * filters by the archived flag, so the card the user was looking at vanishes
 * from the Inactive list with a bare "Renamed" toast and no explanation.
 * `resolveEntitySaveOutcome` now also takes the entity's archived state as it
 * was BEFORE the save (the caller's job — nothing here can know that) and
 * reads its state AFTER the save off the response's `entity.archived_at`, so
 * it can say plainly that the rename also brought the entry back active.
 * Both new inputs are optional and default to today's behaviour, so a caller
 * that doesn't pass them (the Memory page's attribute-edit save) is
 * unaffected.
 *
 * Two surfaces hit the same endpoint and need the same decision: the Memory
 * page's entity-detail panel (`entity-detail.tsx`, attribute/name edits) and
 * the Work board's card rename (`use-card-rename.ts`). Living in `~/lib`
 * rather than under `features/memory` keeps it a neutral, shared home for
 * both callers instead of implying it's Memory-only.
 *
 * Extracted into its own plain module so it's testable in plain node without
 * dragging in the UI component tree — same pure-fn/thin-component split as
 * `entity-delete-copy.ts`'s `buildDeleteEntityConfirmCopy`. `fireEntitySaveToast`
 * is the thin dispatcher around the pure decision — see `undo-toast.ts` for
 * the same "plain module importing `sonner` directly" precedent.
 */

export interface EntitySaveResponse {
  /**
   * FRONT-1109: present only when this response came from a save that tried
   * to implicitly resurrect an archived entity but withheld it because an
   * active duplicate already exists elsewhere — the write itself still
   * landed. Plain-language, caller-safe to show verbatim (e.g. as a toast).
   * Never present on a plain `GET`, and never present when there was
   * nothing to withhold.
   */
  stayedArchivedNote?: string;
  /** FRONT-1154: the entity's `archived_at` as it came back from the save
   *  (post-write state). Present whenever the response is a
   *  `GraphEntityDetail` (i.e. carries the full entity row); absent for a
   *  caller that only has a narrower response shape. */
  entity?: { archived_at?: string | null };
}

export interface EntitySaveOutcome {
  /** Which toast variant to fire. `warning` (amber) — never `error`/destructive
   *  red, because this is not a failure: the write succeeded, only the
   *  implicit un-archive was withheld. */
  toastVariant: 'success' | 'warning';
  message: string;
}

const DEFAULT_SAVED_MESSAGE = 'Saved';

/** Sonner's default toast lifetime (4s) is too short to read the ~30-word
 *  `stayedArchivedNote` warning, and the app's toaster is deliberately
 *  anchored away from the pointer so hover-pause can't save it either. Same
 *  fix already applied to the coding surface's long warnings
 *  (`coding-questions-card.tsx`, `coding-session-modal.tsx`). Plain success
 *  toasts stay on Sonner's default — they're short enough to read in time. */
const WARNING_TOAST_DURATION_MS = 8000;

export function resolveEntitySaveOutcome(
  data: EntitySaveResponse,
  defaultMessage: string = DEFAULT_SAVED_MESSAGE,
  wasArchivedBefore?: boolean,
): EntitySaveOutcome {
  if (data.stayedArchivedNote) {
    return { toastVariant: 'warning', message: data.stayedArchivedNote };
  }
  // FRONT-1154 mirror case: it was archived before, and the save's response
  // shows it's no longer archived AND there was no note explaining a
  // withheld un-archive — so it really did come back active. Say so, rather
  // than a bare "Saved"/"Renamed" that leaves the user to notice the card
  // vanished from the Inactive list on its own.
  // The post-save entity state is REQUIRED evidence here, not an optional
  // extra: `entity` is declared optional for callers with a narrower response
  // shape, and treating "absent" as "not archived" would announce a
  // reactivation nobody confirmed. Without it we fall through to the plain
  // saved message — the whole point of this batch is to stop claiming
  // outcomes we can't see.
  if (wasArchivedBefore && data.entity && !data.entity.archived_at) {
    return { toastVariant: 'success', message: `${defaultMessage} — now active again` };
  }
  return { toastVariant: 'success', message: defaultMessage };
}

/**
 * Thin dispatcher around `resolveEntitySaveOutcome`: resolves the outcome
 * AND fires the right Sonner toast variant with the right duration. Both
 * call sites (`use-card-rename.ts`, `entity-detail.tsx`) had hand-copied the
 * warning-vs-success dispatch; this is the one place it happens now.
 */
export function fireEntitySaveToast(
  data: EntitySaveResponse,
  defaultMessage: string = DEFAULT_SAVED_MESSAGE,
  wasArchivedBefore?: boolean,
): EntitySaveOutcome {
  const outcome = resolveEntitySaveOutcome(data, defaultMessage, wasArchivedBefore);
  if (outcome.toastVariant === 'warning') {
    toast.warning(outcome.message, { duration: WARNING_TOAST_DURATION_MS });
  } else {
    toast.success(outcome.message);
  }
  return outcome;
}
